import { useEffect, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import * as Sentry from '@sentry/react'
import { supabase } from './services/supabase'
import { apiFetch } from './services/api'
import { initPosthog, identifyUser, resetUser } from './services/posthog'
import Landing from './pages/Landing'
import Auth from './pages/Auth'
import AlertsPage from './pages/AlertsPage'
import Settings from './pages/Settings'
import ResetPassword from './pages/ResetPassword'
import PrivacyPolicy from './pages/PrivacyPolicy'
import TermsOfService from './pages/TermsOfService'
import SavedPostingsPage from './pages/SavedPostingsPage'
import AppShell from './components/AppShell'
import TermsGate from './components/TermsGate'

const REDIRECT_KEY = 'postLoginRedirect'

export default function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isRecovery, setIsRecovery] = useState(false)
  // Optimistic: assume accepted until the profile call tells us otherwise.
  // This avoids blocking the whole app on the profile fetch (cold Railway start can be slow).
  const [termsAccepted, setTermsAccepted] = useState<boolean>(true)

  useEffect(() => { initPosthog() }, [])

  useEffect(() => {
    // Preserve deep-link params (?alert=X&posting=Y) across the login flow.
    // When a logged-out user lands on the app with these params, save the full
    // URL so we can restore it after login. Must run before auth state is known.
    const params = new URLSearchParams(window.location.search)
    if (params.get('alert') || params.get('posting')) {
      sessionStorage.setItem(REDIRECT_KEY, window.location.pathname + window.location.search)
    }
  }, [])

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session)
      setLoading(false)

      if (event === 'PASSWORD_RECOVERY') {
        // Recovery token exchange: show reset form in-place, no page reload (reload loses the token)
        setIsRecovery(true)
        return
      }
      if (event === 'SIGNED_IN' && session) {
        Sentry.setUser({ id: session.user.id })
        identifyUser(session.user.id)
        const path = window.location.pathname
        const shouldRedirect = path === '/auth' || path === '/'
        const redirect = shouldRedirect ? (sessionStorage.getItem(REDIRECT_KEY) ?? '/app/alerts') : null
        if (shouldRedirect) sessionStorage.removeItem(REDIRECT_KEY)
        // Check terms before navigating so the fetch isn't aborted by the page change
        apiFetch<{ accepted_terms_version?: string; current_terms_version?: string }>('/account/profile', session)
          .then(profile => {
            const accepted =
              !!profile.current_terms_version &&
              profile.accepted_terms_version === profile.current_terms_version
            if (!accepted) setTermsAccepted(false)
            if (redirect) window.location.href = redirect
          })
          .catch(err => {
            Sentry.captureException(err, { tags: { action: 'load_profile_terms' } })
            setTermsAccepted(false)
            if (redirect) window.location.href = redirect
          })
      }
      if (event === 'SIGNED_OUT') {
        Sentry.setUser(null)
        resetUser()
        window.location.href = '/'
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  if (loading) return <p className="p-10 text-gray-500">Caricamento…</p>

  const path = window.location.pathname

  if (path === '/reset' || isRecovery) return <ResetPassword />
  if (path === '/privacy') return <PrivacyPolicy />
  if (path === '/termini') return <TermsOfService />

  if (session) {
    if (!termsAccepted) {
      return <TermsGate session={session} onAccepted={() => setTermsAccepted(true)} />
    }

    if (path === '/app/settings') {
      return <AppShell session={session} activePath={path}><Settings session={session} /></AppShell>
    }
    if (path === '/app/saved') {
      return <AppShell session={session} activePath={path}><SavedPostingsPage session={session} /></AppShell>
    }
    const params = new URLSearchParams(window.location.search)
    const deepLinkAlertId = params.get('alert') ?? undefined
    const deepLinkPostingId = params.get('posting') ?? undefined
    return (
      <AppShell session={session} activePath={path}>
        <AlertsPage session={session} deepLinkAlertId={deepLinkAlertId} deepLinkPostingId={deepLinkPostingId} />
      </AppShell>
    )
  }

  if (path === '/auth') return <Auth />
  return <Landing />
}
